/**
 * One derivation of "how long has this timer been running" for every surface.
 *
 * TS-TIMER-SYNC (Joe, 2026-09-16: "the time shown in the status bar often
 * doesn't match the actual timesheet in app"). The surfaces had drifted apart
 * because they each counted differently:
 *
 *  - desktop tray (`desktop/tray.js`)  — anchored to the server, re-polled
 *  - mobile Time page                  — anchored to the server, re-synced
 *  - web TimerPanel                    — `setElapsed(s => s + 1)` every second
 *
 * The incrementing counter is the one that lies. A browser throttles
 * `setInterval` in a background or occluded tab, and it doesn't fire at all
 * while the machine is asleep — so the count falls behind real time and, with
 * no re-sync, never catches up. The tray was right; the app was slow.
 *
 * Anchoring fixes the class: recompute from wall-clock on every tick, so a
 * missed tick costs nothing and the next tick is correct again. Pair it with a
 * periodic re-sync to bound drift from clock skew.
 */

export interface TimerSyncAnchor {
  /** The server's `elapsed_seconds` at the moment it was received. */
  serverElapsedSeconds: number
  /** `Date.now()` when that value arrived. */
  syncedAt: number
  /** A paused timer's elapsed is frozen — wall clock must not advance it. */
  paused: boolean
}

export function elapsedFromAnchor(anchor: TimerSyncAnchor | null, now: number = Date.now()): number {
  if (!anchor) return 0
  const base = Math.max(0, Math.round(anchor.serverElapsedSeconds))
  if (anchor.paused) return base
  // A backwards wall-clock jump (NTP correction, user changing the clock) must
  // never wind the displayed time backwards or negative — it would read as the
  // timer breaking. Hold the last value until real time catches up.
  const sinceSync = Math.max(0, Math.floor((now - anchor.syncedAt) / 1000))
  return base + sinceSync
}
